/**
 * Entry application component used to compose providers and render Routes.
 * */

import { BrowserRouter, useHistory } from 'react-router-dom';
import { Routes } from './router/app.router';
import { MaterialThemeProvider } from '../theme';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Helmet from 'react-helmet';
import { getEnvironment } from '../core/env/environment';
import { EnvironmentProvider } from '../core/env/environment.context';
import { EnvironmentType } from '../core/env/environment-type.enum';
import { AuthKitProvider } from '@workos-inc/authkit-react';

dayjs.extend(utc);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // Temp
      refetchOnWindowFocus: false
    }
  }
});

export default function App() {
  const environment = getEnvironment();
  const hostTitle = environment.title;

  const favicon =
    environment.type === EnvironmentType.Production
      ? `/media/brands/${environment.brandId}/favicon.png`
      : `/media/brands/${environment.brandId}/favicon-test.png`;

  const appIcon = `/media/brands/${environment.brandId}/apple-touch-icon.png`;

  console.debug(`Environment ${environment.type} has been set`);
  console.debug(`Brand ${environment.brandId} has been set`);

  return (
    <EnvironmentProvider environment={environment}>
      <Helmet defaultTitle={hostTitle} titleTemplate={`%s - ${hostTitle}`} />
      <Helmet defaultTitle={hostTitle} titleTemplate={`%s - ${hostTitle}`}>
        <link rel="icon" type="image/x-icon" href={favicon} />
      </Helmet>
      <Helmet>
        <link rel="apple-touch-icon" sizes="180x180" href={appIcon} />
      </Helmet>
      <BrowserRouter>
        <AppWithRouter />
      </BrowserRouter>
    </EnvironmentProvider>
  );
}

function AppWithRouter() {
  const history = useHistory();

  return (
    <AuthKitProvider
      clientId={getEnvironment().clientId}
      // Api Hostname is only needed in Production to support custom domains
      apiHostname={getEnvironment().workosApiHostname}
      // If the API Hostname is not set, we are in development mode
      // We must set devMode to true so that refresh tokens work in the test environment
      devMode={getEnvironment().workosApiHostname === undefined}
      onRedirectCallback={({ state }) => {
        if (state?.returnTo) {
          history.replace({
            pathname: state.returnTo
          });
        }
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MaterialThemeProvider>
            <Routes />
          </MaterialThemeProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </AuthKitProvider>
  );
}
